import {
  ItineraryEvent,
  ItineraryEventType,
  ItineraryStop,
  JobType,
  StopType,
} from 'core/graphql/types';
import DonutChart from './donutChart/DonutChart';
import SquareIndicator from './squareIndicator/SquareIndicator';
import LockerMarkerIcon from './lockerMarkerIcon/LockerMarkerIcon';
import PudoMarkerIcon from './pudoMarkerIcon/PudoMarkerIcon';

interface MarkerContentProps {
  itineraryStop: ItineraryStop;
  order: number;
  events: ItineraryEvent[];
}

const MarkerContent: React.FC<MarkerContentProps> = ({
  itineraryStop,
  order,
  events,
}: MarkerContentProps) => {
  if (itineraryStop.jobType === JobType.Pickup) {
    return (
      <SquareIndicator
        stopOrder={order}
        viewBox={22}
        color={events.length > 0 ? '#CC338B' : '#b02025'}
      />
    );
  }

  if (itineraryStop.stopType === StopType.Locker) {
    const hasLockerVisit = events.some(
      (e) => e.eventType === ItineraryEventType.LockerVisit,
    );
    return (
      <LockerMarkerIcon
        stopOrder={order}
        viewBox={25}
        color={hasLockerVisit ? '#3B82F6' : '#b02025'}
      />
    );
  }

  if (itineraryStop.stopType === StopType.Pudo) {
    const hasPudoVisit = events.some(
      (e) => e.eventType === ItineraryEventType.PudoVisit,
    );
    return (
      <PudoMarkerIcon
        stopOrder={order}
        viewBox={25}
        color={hasPudoVisit ? '#F97316' : '#b02025'}
      />
    );
  }

  if (events.length === 0) {
    const slices = [
      {
        id: 1,
        percent: 99.9,
        color: '#b02025',
        label: `${itineraryStop?.identifiers?.split(',')
          .length} awbs left to deliver: ${itineraryStop.identifiers?.split(
          ',',
        )}`,
      },
    ];
    return (
      <DonutChart
        viewBox={15}
        radius={15}
        borderSize={7}
        data={slices}
        stopOrder={order}
        anyApproximatedLocation={false}
        anyR30Addresses={false}
      />
    );
  }

  let slicePercentage = 100;
  const slices = [];
  if (
    itineraryStop?.identifiers &&
    itineraryStop?.identifiers?.split(',').length > 0
  ) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    slicePercentage = 99.9 / itineraryStop.identifiers?.split(',').length;

    const awbsToDeliver =
      itineraryStop.identifiers.split(',').length - events.length;

    slices.push({
      id: 1,
      percent: awbsToDeliver * slicePercentage,
      color: '#b02025',
      label: `${awbsToDeliver} awbs left to deliver: ${itineraryStop.identifiers
        .split(',')
        .filter((x) => !events.map((y) => y.identifier).includes(x))}`,
    });
  }
  events.forEach((event) => {
    slices.push({
      id: slices.length + 1,
      percent: slicePercentage,
      color:
        event.eventType === ItineraryEventType.Delivery
          ? '#228b22'
          : event.eventType === ItineraryEventType.C2CPickup
            ? '#1e3a5f'
            : '#f2c500',
      label: `AWB Id: ${event.identifier}`,
    });
  });

  const anyApproximatedAddress = events.some(
    (address) => address.isLocationApproximated,
  );
  const anyR30Addresses = itineraryStop.containsR30Awbs;

  return (
    <DonutChart
      viewBox={15}
      radius={15}
      borderSize={7}
      data={slices}
      stopOrder={order}
      anyApproximatedLocation={anyApproximatedAddress}
      anyR30Addresses={anyR30Addresses}
    />
  );
};

export default MarkerContent;
